import { useQuery } from '@tanstack/react-query';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import {
  ArrowLeft,
  Check,
  Crown,
  Heart,
  ShieldCheck,
  Sparkles,
  Star,
  User,
  Users,
} from 'lucide-react';
import { useEffect, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { formatBrl } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { useRealtimeInvalidate } from '@/lib/use-realtime-invalidate';

interface PlanoComPrecos {
  seq_plano: number;
  cod_plano: string;
  nom_plano: string;
  des_plano: string | null;
  cod_categoria: 'INDIVIDUAL' | 'CASAL' | 'FAMILIA' | 'EMPRESARIAL';
  qtd_veiculos_min: number;
  qtd_veiculos_max: number;
  qtd_lavagens_mes: number | null;
  qtd_logins_adicionais_max: number;
  ind_permite_pausa: boolean;
  ind_permite_winback: boolean;
  precos:
    | {
        seq_tipo_lavagem: number;
        val_mensalidade: number;
        val_lavagem_avulsa: number | null;
        cod_tipo_lavagem?: string;
        nom_tipo_lavagem?: string;
      }[]
    | null;
}

const CATEGORIA_META: Record<
  string,
  { label: string; icon: ComponentType<{ className?: string }>; cor: string; tagline: string }
> = {
  INDIVIDUAL: {
    label: 'Individual',
    icon: User,
    cor: 'from-sky-500 to-blue-700',
    tagline: 'Pra quem só cuida do seu',
  },
  CASAL: {
    label: 'Casal',
    icon: Heart,
    cor: 'from-rose-500 to-pink-700',
    tagline: 'Dois carros, uma conta',
  },
  FAMILIA: {
    label: 'Família',
    icon: Users,
    // Pastel coral pra família (sem amber, calorosa e suave)
    cor: 'from-orange-400 to-rose-500',
    tagline: 'A frota inteira coberta',
  },
  EMPRESARIAL: {
    label: 'Empresarial',
    icon: Crown,
    cor: 'from-violet-600 to-purple-800',
    tagline: 'Operação que não pode parar',
  },
};

/** Contador animado pra ancorar preço com sensação premium (Apple Wallet vibe). */
function PrecoAnimado({ valor }: { valor: number }) {
  const motionValue = useMotionValue(0);
  const display = useTransform(motionValue, (v) => formatBrl(v));

  useEffect(() => {
    const controls = animate(motionValue, valor, {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
    });
    return controls.stop;
  }, [valor, motionValue]);

  return <motion.span>{display}</motion.span>;
}

export function PlanosPage() {
  const navigate = useNavigate();

  const planos = useQuery({
    queryKey: ['planos-publicos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_planos')
        .select(
          `seq_plano, cod_plano, nom_plano, des_plano, cod_categoria,
           qtd_veiculos_min, qtd_veiculos_max, qtd_lavagens_mes,
           qtd_logins_adicionais_max, ind_permite_pausa, ind_permite_winback,
           precos:tab_planos_precos_lavagem(
             seq_tipo_lavagem, val_mensalidade, val_lavagem_avulsa,
             tipo:tab_tipos_lavagem(cod_tipo_lavagem, nom_tipo_lavagem)
           )`,
        )
        .eq('ind_ativo', true)
        .order('cod_categoria')
        .order('seq_plano');
      if (error) throw error;
      return (data ?? []) as unknown as PlanoComPrecos[];
    },
  });

  useRealtimeInvalidate('tab_planos', ['planos-publicos']);
  useRealtimeInvalidate('tab_planos_precos_lavagem', ['planos-publicos']);

  if (planos.isLoading) {
    return (
      <div className="space-y-3 p-4 pb-24">
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-72 rounded-3xl" />
        <Skeleton className="h-72 rounded-3xl" />
      </div>
    );
  }

  if (planos.error) {
    return (
      <div className="p-4">
        <p className="text-sm text-rose-600">
          Erro ao carregar planos: {(planos.error as Error).message}
        </p>
      </div>
    );
  }

  if (!planos.data || planos.data.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          variant="hero"
          icon={Sparkles}
          title="Em breve, planos para você"
          description="Estamos preparando combinações sob medida. Volte logo!"
        />
      </div>
    );
  }

  // Heurística: o plano "FAMILIA" (ou o do meio) recebe destaque "RECOMENDADO"
  const idxRecomendado =
    planos.data.findIndex((p) => p.cod_categoria === 'FAMILIA') >= 0
      ? planos.data.findIndex((p) => p.cod_categoria === 'FAMILIA')
      : Math.floor(planos.data.length / 2);

  return (
    <div className="relative min-h-screen overflow-hidden p-4 pb-24">
      {/* Blobs decorativos */}
      <div
        aria-hidden
        className="octa-blob bg-octa-gradient pointer-events-none absolute -left-32 -top-32 h-72 w-72"
        style={{ opacity: 0.3 }}
      />
      <div
        aria-hidden
        className="octa-blob bg-octa-accent pointer-events-none absolute -right-32 top-1/3 h-64 w-64"
        style={{ opacity: 0.25, animationDelay: '-6s' }}
      />

      <div className="relative z-10 mx-auto max-w-2xl space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-muted-foreground -ml-2 mb-3 h-8 px-2"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Voltar
          </Button>
          <h1 className="text-display-md font-extrabold tracking-tight">
            Escolha o seu <span className="text-octa-gradient">plano perfeito</span>
          </h1>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
            Lavagens ilimitadas, pause quando quiser, sem fidelidade. Cancele a qualquer momento.
          </p>
        </motion.div>

        {/* Cards de planos */}
        <div className="space-y-5">
          {planos.data.map((p, idx) => {
            const meta = CATEGORIA_META[p.cod_categoria] ?? CATEGORIA_META.INDIVIDUAL!;
            const precos = p.precos ?? [];
            const menorPreco =
              precos.length > 0
                ? Math.min(...precos.map((pr) => Number(pr.val_mensalidade)))
                : null;
            const recomendado = idx === idxRecomendado;

            return (
              <motion.div
                key={p.seq_plano}
                initial={{ opacity: 0, y: 24, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.55,
                  delay: 0.08 + idx * 0.08,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={{ y: -3 }}
                className="relative"
              >
                {recomendado && (
                  <>
                    {/* Conic glow ring por trás do card */}
                    <div
                      aria-hidden
                      className="octa-conic-ring absolute -inset-[2px] -z-10 rounded-[26px] opacity-70 blur-[2px]"
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 0.35, type: 'spring', stiffness: 280, damping: 18 }}
                      className="absolute -top-3 left-1/2 z-20 -translate-x-1/2"
                    >
                      <div className="bg-octa-accent shadow-octa-lg shadow-octa-glow flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                        <Star className="h-3 w-3 fill-current" />
                        Mais popular
                      </div>
                    </motion.div>
                  </>
                )}
                <Card
                  variant={recomendado ? 'wallet' : 'default'}
                  className={`octa-spotlight overflow-hidden rounded-3xl ${
                    recomendado ? 'shadow-octa-lg' : ''
                  }`}
                >
                  {/* Mesh decorativo no card recomendado */}
                  {recomendado && (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 opacity-30"
                      style={{
                        backgroundImage:
                          'radial-gradient(at 100% 0%, rgba(255,255,255,0.4) 0px, transparent 50%), radial-gradient(at 0% 100%, rgba(255,255,255,0.15) 0px, transparent 50%)',
                      }}
                    />
                  )}
                  <CardContent
                    className={`relative space-y-4 p-6 ${recomendado ? 'text-white' : ''}`}
                  >
                    {/* Header com icone + categoria */}
                    <div className="flex items-start justify-between">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${meta.cor} text-white shadow-md ${
                          recomendado ? 'ring-2 ring-white/40' : ''
                        }`}
                      >
                        <meta.icon className="h-6 w-6" />
                      </div>
                      <div className="text-right">
                        <span
                          className={`text-[10px] font-bold uppercase tracking-[0.18em] ${
                            recomendado ? 'text-white/80' : 'text-muted-foreground'
                          }`}
                        >
                          {meta.label}
                        </span>
                        <p
                          className={`mt-0.5 text-[11px] ${
                            recomendado ? 'text-white/70' : 'text-muted-foreground/80'
                          }`}
                        >
                          {meta.tagline}
                        </p>
                      </div>
                    </div>

                    {/* Nome */}
                    <div>
                      <h2 className="text-2xl font-extrabold tracking-tight">{p.nom_plano}</h2>
                      {p.des_plano && (
                        <p
                          className={`mt-1 text-sm ${
                            recomendado ? 'text-white/85' : 'text-muted-foreground'
                          }`}
                        >
                          {p.des_plano}
                        </p>
                      )}
                    </div>

                    {/* Preço destaque com contador animado */}
                    {menorPreco !== null && (
                      <div
                        className={`flex items-baseline gap-1 rounded-2xl px-4 py-3 ${
                          recomendado ? 'bg-white/15 backdrop-blur-sm' : 'bg-muted/50'
                        }`}
                      >
                        <span
                          className={`text-xs font-medium ${
                            recomendado ? 'text-white/75' : 'text-muted-foreground'
                          }`}
                        >
                          a partir de
                        </span>
                        <span
                          className={`ml-1 text-4xl font-extrabold tabular-nums leading-none ${
                            recomendado ? 'text-white drop-shadow' : 'text-octa-gradient'
                          }`}
                        >
                          <PrecoAnimado valor={menorPreco} />
                        </span>
                        <span
                          className={`text-sm font-medium ${
                            recomendado ? 'text-white/80' : 'text-muted-foreground'
                          }`}
                        >
                          /mês
                        </span>
                      </div>
                    )}

                    {/* Benefícios */}
                    <ul className="space-y-2.5 text-sm">
                      {[
                        p.qtd_veiculos_max === 1
                          ? '1 veículo na assinatura'
                          : `${p.qtd_veiculos_min} a ${p.qtd_veiculos_max} veículos inclusos`,
                        p.qtd_lavagens_mes === null
                          ? 'Lavagens ilimitadas no mês'
                          : `${p.qtd_lavagens_mes} lavagens por mês`,
                        p.qtd_logins_adicionais_max > 0 &&
                          `Convide até ${p.qtd_logins_adicionais_max} pessoa(s) na mesma conta`,
                        p.ind_permite_pausa && 'Pause a assinatura quando precisar',
                        p.ind_permite_winback && 'Promoções exclusivas de retorno',
                        'Sem fidelidade, cancele quando quiser',
                      ]
                        .filter(Boolean)
                        .map((beneficio, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <div
                              className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                                recomendado ? 'bg-white/25 backdrop-blur-sm' : 'bg-octa-gradient'
                              }`}
                            >
                              <Check
                                className={`h-3 w-3 ${recomendado ? 'text-white' : 'text-white'}`}
                                strokeWidth={3}
                              />
                            </div>
                            <span className={recomendado ? 'text-white/95' : ''}>{beneficio}</span>
                          </li>
                        ))}
                    </ul>

                    {/* Detalhes preços */}
                    {precos.length > 1 && (
                      <details className="text-xs">
                        <summary
                          className={`cursor-pointer select-none font-semibold ${
                            recomendado
                              ? 'text-white/85 hover:text-white'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Ver preços por tipo de lavagem ({precos.length})
                        </summary>
                        <ul
                          className={`mt-2 space-y-1.5 rounded-xl p-3 ${
                            recomendado ? 'bg-white/10' : 'bg-muted/40'
                          }`}
                        >
                          {precos.map((pr) => {
                            const tipo = (pr as unknown as { tipo?: { nom_tipo_lavagem?: string } })
                              .tipo;
                            return (
                              <li key={pr.seq_tipo_lavagem} className="flex justify-between">
                                <span>{tipo?.nom_tipo_lavagem ?? '—'}</span>
                                <span className="font-semibold tabular-nums">
                                  {formatBrl(pr.val_mensalidade)}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </details>
                    )}

                    {/* CTA */}
                    <motion.div whileTap={{ scale: 0.97 }} whileHover={{ scale: 1.01 }}>
                      <Button
                        size="xl"
                        onClick={() => {
                          toast.success(`${p.nom_plano} selecionado!`, {
                            description: 'Em breve liberamos a contratação direto por aqui.',
                          });
                        }}
                        className={`w-full ${
                          recomendado
                            ? 'text-primary shadow-octa-lg hover:text-primary bg-white hover:bg-white/95'
                            : 'bg-octa-gradient shadow-octa-glow text-white'
                        }`}
                      >
                        Quero o {p.nom_plano}
                      </Button>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Garantia */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <Card variant="glass" className="rounded-2xl border-white/40">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="bg-octa-gradient shadow-octa-glow flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
                <ShieldCheck className="h-5 w-5 text-white" />
              </div>
              <div className="text-xs">
                <p className="font-bold">Garantia de satisfação</p>
                <p className="text-muted-foreground mt-0.5">
                  Não amou? Cancela em 1 toque. Sem multa, sem burocracia.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
